<template>
  <ion-page>
    <ion-content>
      <div class="main-container">
        <div class="px-16 desk:px-0 pb-40">
          <div class="pt-44 pb-32 text-start headline-xl">
            {{ active ? 'Förhandsgranska anmälan' : 'Inskickad anmälan' }}
          </div>
          <SummaryCard
            class="mb-24"
            :step="1"
            :fields="visibleObligatoryFields"
            :is-mandatory="true"
            :active="active"
            button-label="Ändra"
            label="Beskriv händelsen"
            @on-edit="editStep"
          />
          <SummaryCard
            v-if="optionalFields.length > 0"
            class="mb-24"
            :step="2"
            :fields="visibleOptionalFields"
            :active="active"
            button-label="Komplettera din anmälan"
            label="Kompletterande frågor"
            @on-edit="editStep"
          />
          <div v-if="incident" class="bg-white rounded-xl">
            <div class="headline-lg px-16 py-24 border-b border-light text-start">
              {{ active ? 'Slutför anmälan' : 'Ytterligare uppgifter' }}
            </div>
            <div class="px-16 py-24 text-start">
              <div class="headline-small mb-12">
                {{ 'Datum' }}
              </div>
              <div class="base text-dark">{{ `${moment(incident.date_start || incident.created_at).format('DD MMM YYYY, HH:mm')} - ${moment(incident.date_end || incident.created_at).format('DD MMM YYYY, HH:mm')}` }}</div>
            </div>
            <div v-if="user.workplace.id" class="px-16 py-24 text-start border-t border-light">
              <div class="headline-small mb-12">
                {{ 'Ditt företag' }}
              </div>
              <div class="base text-dark">{{ user.workplace.name }}</div>
              <div class="base text-dark">{{ user.workplace.address }},</div>
              <div class="base text-dark">{{ `${user.workplace.zip} ${user.workplace.city}` }}</div>
              <div class="mt-24">
                <div v-if="selectedSector" class="flex items-center">
                  <ion-icon
                    aria-hidden="true"
                    :icon="checkmarkCircle"
                    class="text-primary-default w-14 h-14 mr-8 mb-2"
                  />
                  <div class="base text-dark">{{ selectedSector.nicename }}</div>
                </div>
                <div class="flex items-center">
                  <ion-icon
                    aria-hidden="true"
                    :icon="checkmarkCircle"
                    class="text-primary-default w-14 h-14 mr-8 shrink-0 mb-2"
                  />
                  <div class="base text-dark">
                    {{ incident.location_address
                      ? `Incidenten inträffade på: ${incident.location_address}, ${incident.location_zip} ${incident.location_city}`
                      : 'Incidenten inträffade på mitt arbetsställe'
                    }}
                  </div>
                </div>
              </div>
            </div>
            <div v-if="user.first_name && user.last_name && user.email" class="px-16 py-24 text-start border-t border-light">
              <div class="headline-small mb-12">
                {{ 'Dina uppgifter' }}
              </div>
              <div class="base text-dark">{{ user.first_name }}</div>
              <div class="base text-dark">{{ user.last_name }}</div>
              <div class="base text-dark">{{ user.email }}</div>
              <div class="base text-dark">{{ user.phone }}</div>
            </div>
            <div v-if="active" class="px-16 pb-24">
              <Button
                text="Ändra"
                :icon="arrowForward"
                type="secondary"
                @click="editStep(3)"
              />
            </div>
          </div>
        </div>
        <div class="bg-white border-t border-light desk:border-none desk:rounded-xl px-16 pt-40 pb-128">
          <div v-if="active" class="bg-peach rounded-lg px-24 py-32 text-start">
            <div class="headline-lg">Innan du skickar iväg din anmälan…</div>
            <div class="text-sm text-dark mt-12">Nedan kan du välja att skicka din anmälan till polisen eller till ytterligare mottagare.</div>
            <SelectInput
              v-if="incident.police_enabled"
              ref="policeSelect"
              class="mt-24 bg-white"
              :select-label="'Välj polis'"
              :options="policeEmailOptions"
              :is-optional="true"
              :outside-trigger-open="shouldOpenPolice"
              @change="selectPoliceContact"
            />
            <SelectInput
              v-if="incident.insurance_enabled"
              class="mt-24 bg-white"
              :select-label="'Välj försäkringsbolag'"
              :options="insuranceEmailOptions"
              :is-optional="true"
              @change="selectInsuranceContact"
            />
            <SelectInput
              class="mt-24 bg-white"
              :select-label="'Välj grupper'"
              :options="formattedUserGroups"
              :is-optional="true"
              :multiselect="true"
              @change="selectGroupContact"
            />
            <div class="mt-40">
              <div class="headline-small">Skicka till ytterligare mottagare:</div>
              <div>
                <TextInput
                  v-for="(additionalEmailInput, index) in additionalEmails"
                  :key="index"
                  class="mt-16 bg-white"
                  placeholder="E-postadress"
                  :value="additionalEmailInput.email"
                  :invalid="!!additionalEmailInput.email && additionalEmailsValidated && !emailValid(additionalEmailInput.email)"
                  @on-input="(input) => modifyAdditionalEmails(index, input)"
                />
                <div
                  class="text-base font-medium underline mt-16"
                  :class="{
                    'text-primary-default': !darkText,
                    'text-black': darkText,
                  }"
                  @click="addAdditionalEmail"
                >
                  {{ 'Lägg till fler' }}
                </div>
              </div>
            </div>
          </div>
          <div v-else class="bg-peach rounded-lg px-24 py-32 text-start">
            <div class="headline-lg">Ytterligare mottagare</div>
            <div v-if="incident && incident.send_to_police" class="text-sm text-dark mt-12 flex items-center">
              <ion-icon
                aria-hidden="true"
                :icon="checkmarkCircle"
                class="text-primary-default w-14 h-14 mr-8 mb-2"
              />
              Skickad till polis
            </div>
            <div v-if="incident && incident.insurance_email_id" class="text-sm text-dark mt-12 flex items-center">
              <ion-icon
                aria-hidden="true"
                :icon="checkmarkCircle"
                class="text-primary-default w-14 h-14 mr-8 mb-2"
              />
              Skickad till försäkringsbolag
            </div>
            <div v-if="incident && incident.groups && incident.groups.length > 1" class="text-sm text-dark mt-12 flex items-center">
              <ion-icon
                aria-hidden="true"
                :icon="checkmarkCircle"
                class="text-primary-default w-14 h-14 mr-8 mb-2"
              />
              Delad med grupp
            </div>
            <div v-if="incident && incident.additional_emails.length > 0" class="text-sm text-dark mt-12 flex items-center">
              <ion-icon
                aria-hidden="true"
                :icon="checkmarkCircle"
                class="text-primary-default w-14 h-14 mr-8 mb-2 shrink-0"
              />
              {{ `Även skickad till: ${additionalEmailLabels.join(', ')}` }}
            </div>
          </div>
          <div v-if="active" class="pt-40">
            <Button
              type="primary"
              :text="incidentStore.editingCompletedIncident ? 'Uppdatera anmälan' : 'Skicka anmälan'"
              :loading="incidentLoading"
              @click="submitIncident"
            />
            <div class="text-xs text-dark mt-24">
              Genom att använda den här applikationen godkänner du våra <a class="underline cursor-pointer" href="https://www.svenskhandel.se/anvandarvillkor/">Användarvillkor</a> och <a class="underline cursor-pointer" href="https://www.svenskhandel.se/">Integritetspolicy</a>
            </div>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { IonPage, IonContent, IonIcon, alertController } from '@ionic/vue';
import { arrowForward, checkmarkCircle } from 'ionicons/icons';
import { useIncidentStore } from '@/store/incident';
import { storeToRefs } from 'pinia';
import { AdditionalEmail, Sector } from '@/constants/types/api';
import { computed, onMounted, ref, watch } from 'vue';
import Button from '@/components/Button.vue';
import SummaryCard from '@/components/SummaryCard.vue';
import moment from 'moment';
import { useFilterStore } from '@/store/filters';
import SelectInput from '@/components/form-elements/SelectInput.vue';
import TextInput from '@/components/form-elements/TextInput.vue';
import IncidentStatus from '@/constants/incident-status';
import { useUserStore } from '@/store/user';
import { emailValid } from '@/utils/validation';

const incidentStore = useIncidentStore();
const filterStore = useFilterStore();
const userStore = useUserStore();
const { optionalFields, obligatoryFields, incident, user, policeEmails, insuranceEmails, additionalEmailsValidated, incidentLoading, editingCompletedIncident } = storeToRefs(incidentStore);
const { userGroups, darkText } = storeToRefs(userStore);
const additionalEmails = ref<AdditionalEmail[]>([{ email: '' }]);
const hasPromptedPolice = ref(false);
const shouldOpenPolice = ref(false);

const policeSelect = ref<HTMLElement>();

const active = computed(() => {
  return incident.value && (incident.value.status !== IncidentStatus.Completed || editingCompletedIncident.value);
});

const visibleObligatoryFields = computed(() => {
  return obligatoryFields.value.filter(field => incidentStore.hasFieldAnswer(field));
});

const visibleOptionalFields = computed(() => {
  return optionalFields.value.filter(field => incidentStore.hasFieldAnswer(field));
});

const editStep = (step: number) => {
  incidentStore.onSet(step);
};

const selectedSector = computed(() => {
  return filterStore.sectors().find(sector => sector.id === incident.value.user.company.sector) as Sector;
});

const formattedUserGroups = computed(() => {
  return userGroups.value.map(group => {
    return {
      value: group.id,
      label: group.name,
    };
  });
});

const selectPoliceContact = (value: number | string) => {
  hasPromptedPolice.value = !!value;
  incidentStore.setField('police_email_id', value as number);
};

const selectInsuranceContact = (value: number | string) => {
  incidentStore.setField('insurance_email_id', value as number);
};

const selectGroupContact = (value: Array<number | string>) => {
  incidentStore.setGroupIds(value);
};

const modifyAdditionalEmails = (index: number, input: string) => {
  const currentEmail = additionalEmails.value[index];
  additionalEmails.value[index] = {
    ...(currentEmail || { email: '' }),
    email: input,
  };
  incidentStore.setAdditionalEmails(additionalEmails.value);
};

const addAdditionalEmail = () => {
  additionalEmails.value = [...additionalEmails.value, { email: '' }];
};

const policeEmailOptions = computed(() => {
  return policeEmails.value.map(email => ({ value: email.id, label: email.name }));
});

const insuranceEmailOptions = computed(() => {
  return insuranceEmails.value.map(email => ({ value: email.id, label: email.name }));
});

const additionalEmailLabels = computed(() => {
  return additionalEmails.value.map(additionalEmail => additionalEmail.email).filter(Boolean);
});

const submitIncident = async() => {
  if (!hasPromptedPolice.value && incident.value.police_enabled) {
    const alert = await alertController.create({
      header: 'Anmäl händelsen till polisen',
      subHeader: 'Vill du skicka händelsen till polisen?',
      buttons: [
        {
          text: 'Nej',
          role: 'confirm',
          handler: () => {
            hasPromptedPolice.value = true;
          },
        },
        {
          text: 'Ja',
          handler: () => {
            hasPromptedPolice.value = true;
            shouldOpenPolice.value = true;
          },
        },
      ],

    });

    await alert.present();
  } else {
    await incidentStore.completeIncident(incidentStore.editingCompletedIncident ? 4 : null);
  }
};

onMounted(() => {
  if (incident.value) {
    additionalEmails.value = incident.value.additional_emails;
  }
});

watch(
  incident,
  () => {
    if (incident.value) {
      additionalEmails.value = incident.value.additional_emails;
    }
  }
);

</script>
<style scoped>
ion-content {
  --background: var(--ion-color-smoke);
}
</style>
